import { KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Typography } from '../../../Typography';
import {
  addMonthsClamped,
  buildMonthDays,
  clampDateToRange,
  isDateDisabled,
  isDateInRange,
  isSameDay,
  startOfMonth,
  toDateKey,
} from '../DateInput.helpers';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface CalendarGridProps {
  endDate?: Date | null;
  maxDate?: Date | null;
  minDate?: Date | null;
  onDateSelect: (
    date: Date,
    event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
  ) => void;
  selected?: Date | null;
  selectsRange?: boolean;
  startDate?: Date | null;
  visibleMonth: Date;
  onVisibleMonthChange: (date: Date) => void;
}

export const CalendarGrid = ({
  endDate,
  maxDate,
  minDate,
  onDateSelect,
  selected,
  selectsRange,
  startDate,
  visibleMonth,
  onVisibleMonthChange,
}: CalendarGridProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const initialFocusDate = selected ?? startDate ?? new Date();
  const [focusedDate, setFocusedDate] = useState(initialFocusDate);
  const days = useMemo(() => buildMonthDays(visibleMonth), [visibleMonth]);

  useEffect(() => {
    const button = calendarRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${toDateKey(focusedDate)}"]`,
    );
    button?.focus();
  }, [focusedDate, visibleMonth]);

  const moveFocus = (date: Date) => {
    const boundedDate = clampDateToRange(date, minDate, maxDate);
    setFocusedDate(boundedDate);
    if (
      boundedDate.getMonth() !== visibleMonth.getMonth() ||
      boundedDate.getFullYear() !== visibleMonth.getFullYear()
    ) {
      onVisibleMonthChange(startOfMonth(boundedDate));
    }
  };

  const handleDayKeyDown = (event: KeyboardEvent<HTMLButtonElement>, date: Date) => {
    const nextDate = new Date(date);
    if (event.key === 'ArrowLeft') nextDate.setDate(date.getDate() - 1);
    else if (event.key === 'ArrowRight') nextDate.setDate(date.getDate() + 1);
    else if (event.key === 'ArrowUp') nextDate.setDate(date.getDate() - 7);
    else if (event.key === 'ArrowDown') nextDate.setDate(date.getDate() + 7);
    else if (event.key === 'Home') nextDate.setDate(date.getDate() - date.getDay());
    else if (event.key === 'End') nextDate.setDate(date.getDate() + (6 - date.getDay()));
    else if (event.key === 'PageUp') nextDate.setTime(addMonthsClamped(date, -1).getTime());
    else if (event.key === 'PageDown') nextDate.setTime(addMonthsClamped(date, 1).getTime());
    else return;

    event.preventDefault();
    moveFocus(nextDate);
  };

  return (
    <div ref={calendarRef}>
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous month"
          className="size-8 rounded-md border-none bg-transparent text-lg hover:bg-secondary-300"
          onClick={() =>
            onVisibleMonthChange(
              new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1),
            )
          }
        >
          ‹
        </button>
        <Typography className="flex-1 text-center" variant="h4">
          {DateTime.fromJSDate(visibleMonth).toFormat('LLLL yyyy')}
        </Typography>
        <button
          type="button"
          aria-label="Next month"
          className="size-8 rounded-md border-none bg-transparent text-lg hover:bg-secondary-300"
          onClick={() =>
            onVisibleMonthChange(
              new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1),
            )
          }
        >
          ›
        </button>
      </div>

      <div role="grid" aria-label="Calendar" className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((weekday) => (
          <Typography
            key={weekday}
            role="columnheader"
            className="py-1 text-center !text-secondary-700"
          >
            {weekday}
          </Typography>
        ))}

        {days.map((date, index) => {
          if (!date)
            return <span key={`empty-${index}`} role="gridcell" aria-hidden className="size-8" />;

          const disabled = isDateDisabled(date, minDate, maxDate);
          const isSelected =
            isSameDay(date, selected) ||
            isSameDay(date, startDate) ||
            (selectsRange && isSameDay(date, endDate));
          const isInRange = selectsRange && isDateInRange(date, startDate, endDate);
          const isToday = isSameDay(date, new Date());

          return (
            <button
              key={toDateKey(date)}
              type="button"
              role="gridcell"
              data-date={toDateKey(date)}
              disabled={disabled}
              aria-label={DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_FULL)}
              aria-selected={isSelected || isInRange}
              tabIndex={isSameDay(date, focusedDate) ? 0 : -1}
              className={[
                'size-8 rounded-md border border-solid text-center transition',
                disabled
                  ? 'cursor-not-allowed border-transparent text-secondary-400'
                  : 'cursor-pointer border-transparent hover:bg-secondary-300',
                isInRange ? 'bg-primary-100' : '',
                isSelected ? '!bg-primary-400 !text-secondary-100' : '',
                isToday && !isSelected ? '!border-primary-400' : '',
              ].join(' ')}
              onFocus={() => setFocusedDate(date)}
              onKeyDown={(event) => handleDayKeyDown(event, date)}
              onClick={(event) => onDateSelect(date, event)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
